import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SpirisIcon from '../../../Components/Fundamentals/Icon/SpirisIcon';
import Spinner from '../../../Components/Fundamentals/Spinner/Spinner';
import ConfirmDeleteModal from '../../../Components/Modals/ConfirmDeleteModal/ConfirmDeleteModal';
import PackageDetailHeader from '../../../Components/Voss/Packages/PackageDetailHeader';
import PackageEditDrawer from '../../../Components/Voss/Packages/PackageEditDrawer';
import PackageOverviewTab from '../../../Components/Voss/Packages/PackageOverviewTab';
import PackagePlansTab from '../../../Components/Voss/Packages/PackagePlansTab';
import PackagePlanEditorDrawer from '../../../Components/Voss/Packages/PackagePlanEditorDrawer';
import CopyPlanModal from '../../../Components/Voss/Packages/CopyPlanModal';
import PackageTiersTab from '../../../Components/Voss/Packages/PackageTiersTab';
import PackageTierEditorDrawer from '../../../Components/Voss/Packages/PackageTierEditorDrawer';
import PackageChargePlansTab from '../../../Components/Voss/Packages/PackageChargePlansTab';
import PackageChargePlanDrawer from '../../../Components/Voss/Packages/PackageChargePlanDrawer';
import PackageChargePlanEditorDrawer from '../../../Components/Voss/Packages/PackageChargePlanEditorDrawer';
import { chargePlanTitle, toCreateChargePlanRequest, toUpdateChargePlanRequest, withRevenueRecognition } from '../../../Components/Voss/Packages/chargePlanUtils';
import { nextTierRank, planTierSave, toCreateTierRequest } from '../../../Components/Voss/Packages/tierUtils';
import PackageSalesChannelsTab from '../../../Components/Voss/Packages/PackageSalesChannelsTab';
import PackageSalesChannelDrawer from '../../../Components/Voss/Packages/PackageSalesChannelDrawer';
import PackageSalesChannelRemoveModal from '../../../Components/Voss/Packages/PackageSalesChannelRemoveModal';
import PackageScheduledChangesTab from '../../../Components/Voss/Packages/PackageScheduledChangesTab';
import PackageScheduledChangeDrawer from '../../../Components/Voss/Packages/PackageScheduledChangeDrawer';
import CancelScheduledChangeModal from '../../../Components/Voss/Packages/CancelScheduledChangeModal';
import PackageVersionStatusModal from '../../../Components/Voss/Packages/PackageVersionStatusModal';
import CreatePackageVersionModal from '../../../Components/Voss/Packages/CreatePackageVersionModal';
import PackageVersionHistoryDrawer from '../../../Components/Voss/Packages/PackageVersionHistoryDrawer';
import TabsBase from '../../../Components/Tabs/Tabs';
import { PACKAGE_DETAIL_TABS, allowedTransitions, isFullyEditable } from '../../../Components/Voss/Packages/packageConstants';
import { numberVersions, versionLabelOf, versionTransitionConfig } from '../../../Components/Voss/Packages/versionUtils';
import packageActions from '../../../Actions/Voss/packageActions';
import packageUpdateActions from '../../../Actions/Voss/packageUpdateActions';
import packagePlanActions from '../../../Actions/Voss/packagePlanActions';
import packageTierActions from '../../../Actions/Voss/packageTierActions';
import packageChargePlanActions from '../../../Actions/Voss/packageChargePlanActions';
import packageScheduledChangeActions from '../../../Actions/Voss/packageScheduledChangeActions';
import targetGroupActions from '../../../Actions/Voss/targetGroupActions';
import commonActions from '../../../Actions/commonActions';
import { useAppDispatch } from '../../../Store/types';
import useModal from '../../../Hooks/useModal';
import Modals from '../../../Constants/modals';
import { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import type {
    ChargePlanDraft,
    PackageChargePlan,
    PackagePlan,
    PackagePlanRequest,
    PackageSalesChannel,
    PackageScheduledChange,
    PackageScheduledChangeRequest,
    PackageSearchHit,
    PackageTier,
    ProductName,
    PlanCopySource,
    RootState,
    TierDraft,
    TierRankChange,
    UpdatePackageSalesChannelRequest,
} from '../../../Components/Voss/Packages/Packages.types';
import type { VossUseRevenueRecognition } from '../../../Constants/VOSS/vossUseRevenueRecognition';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Tabs = TabsBase as any;

interface PlanEditorState {
    plan?: PackagePlan;
    copySource?: PlanCopySource;
}

interface ScheduledChangeEditorState {
    change?: PackageScheduledChange;
}

const PackageDetailContainer = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { packageId } = useParams<{ packageId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const versionIdParam = searchParams.get('versionId');
    const versionId = versionIdParam === null || versionIdParam.length === 0 ? undefined : versionIdParam;
    const { isModalOpen, openModal, closeModal } = useModal();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [planEditor, setPlanEditor] = useState<PlanEditorState | null>(null);
    const [isSavingPlan, setIsSavingPlan] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<PackagePlan | null>(null);
    const [isDeletingPlan, setIsDeletingPlan] = useState(false);
    const [tierToDelete, setTierToDelete] = useState<PackageTier | null>(null);
    const [isDeletingTier, setIsDeletingTier] = useState(false);
    const [tierEditor, setTierEditor] = useState<{ tier?: PackageTier } | null>(null);
    const [openChargePlanId, setOpenChargePlanId] = useState<string | null>(null);
    const [savingRevenueGroupId, setSavingRevenueGroupId] = useState<string | null>(null);
    const [chargePlanEditor, setChargePlanEditor] = useState<{ chargePlan?: PackageChargePlan; tierId?: string; planId?: string } | null>(null);
    const [isSavingChargePlan, setIsSavingChargePlan] = useState(false);
    const [isSavingTier, setIsSavingTier] = useState(false);
    const [isReorderingTiers, setIsReorderingTiers] = useState(false);
    const [productCatalog, setProductCatalog] = useState<ProductName[]>();
    const [salesChannelDrawer, setSalesChannelDrawer] = useState<{ channel?: PackageSalesChannel }>();
    const [channelToRemove, setChannelToRemove] = useState<PackageSalesChannel>();
    const [isSavingSalesChannel, setIsSavingSalesChannel] = useState(false);
    const [scheduledChangeEditor, setScheduledChangeEditor] = useState<ScheduledChangeEditorState | null>(null);
    const [isSavingScheduledChange, setIsSavingScheduledChange] = useState(false);
    const [changeToCancel, setChangeToCancel] = useState<PackageScheduledChange | null>(null);
    const [isCancellingChange, setIsCancellingChange] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<VossVersionStatus | null>(null);
    const [isChangingStatus, setIsChangingStatus] = useState(false);
    const [isCreatingVersion, setIsCreatingVersion] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const shownPackageIdRef = useRef<string | undefined>(undefined);
    const salesChannelsReloadRef = useRef<AbortController | null>(null);
    const vossPackage = useSelector((state: RootState) => state.PackageReducer.package);
    const packageVersions = useSelector((state: RootState) => state.PackageReducer.packageVersions);
    const packages = useSelector((state: RootState) => state.PackageReducer.packages);
    const storedPlans = useSelector((state: RootState) => state.PackageReducer.packagePlans);
    const storedTiers = useSelector((state: RootState) => state.PackageReducer.packageTiers);
    const storedChargePlans = useSelector((state: RootState) => state.PackageReducer.packageChargePlans);
    const storedScheduledChanges = useSelector((state: RootState) => state.PackageReducer.packageScheduledChanges);
    const reloadedSalesChannels = useSelector((state: RootState) => state.PackageReducer.packageSalesChannels);
    const salesChannelCatalog = useSelector((state: RootState) => state.PackageReducer.salesChannelCatalog);
    const isLoading = useSelector((state: RootState) => state.CommonReducer.dataLoading.package);
    const isLoadingPlans = useSelector((state: RootState) => state.CommonReducer.dataLoading.packagePlans ?? false);
    const isLoadingTiers = useSelector((state: RootState) => state.CommonReducer.dataLoading.packageTiers ?? false);
    const isLoadingScheduledChanges = useSelector((state: RootState) => state.CommonReducer.dataLoading.packageScheduledChanges ?? false);
    const targetGroups = useSelector((state: RootState) => state.TargetGroupsReducer.targetGroups) ?? [];

    const basePath = `/settings/packages/${packageId}`;
    const activePath = location.pathname.replace(basePath, '').replace(/^\//, '');
    const isSalesChannelsTab = activePath === 'sales-channels';
    const isTiersTab = activePath === 'tiers';
    const isChargePlansTab = activePath === 'charge-plans';

    const isCopyingPlan = isModalOpen(Modals.CopyPackagePlan);
    const isConfirmingPlanDelete = isModalOpen(Modals.DeletePackagePlan) && planToDelete !== null;
    const isConfirmingTierDelete = isModalOpen(Modals.DeletePackageTier) && tierToDelete !== null;
    const isConfirmingChargePlanDelete = isModalOpen(Modals.DeletePackageChargePlan) && chargePlanEditor?.chargePlan !== undefined;
    const isConfirmingChannelRemove = isModalOpen(Modals.ConfirmDeleteModal) && channelToRemove !== undefined;
    const isConfirmingChangeCancel = isModalOpen(Modals.CancelPackageScheduledChange) && changeToCancel !== null;
    const isConfirmingStatusChange = isModalOpen(Modals.ChangePackageVersionStatus) && pendingStatus !== null;
    const isCreateVersionOpen = isModalOpen(Modals.CreatePackageVersion);

    useEffect(() => {
        if (packageId === undefined) return;

        // the store keeps the previously viewed package, which would render under the new id until the fetch lands
        void dispatch(packageActions.clear('package'));
        void dispatch(packageActions.getPackage(packageId, versionId));
    }, [dispatch, packageId, versionId]);

    useEffect(() => {
        if (packageId === undefined) return;

        shownPackageIdRef.current = packageId;
        void dispatch(packageActions.clear('packageVersions'));
        void dispatch(packageActions.getPackageVersions(packageId));
        void dispatch(packageActions.clear('packageSalesChannels'));

        return () => {
            shownPackageIdRef.current = undefined;
            salesChannelsReloadRef.current?.abort();
        };
    }, [dispatch, packageId]);

    useEffect(() => {
        void dispatch(targetGroupActions.getTargetGroups());
    }, [dispatch]);

    // keyed on the loaded package's version rather than the URL, so an omitted versionId resolves to the same version as the package
    const loadedVersionId = vossPackage?.versionId;
    useEffect(() => {
        if (packageId === undefined || loadedVersionId === undefined) return;

        // an editor opened for another version would otherwise save its id against this one
        setPlanEditor(null);
        setPlanToDelete(null);
        setTierToDelete(null);
        setTierEditor(null);
        setOpenChargePlanId(null);
        setChargePlanEditor(null);
        setScheduledChangeEditor(null);
        setChangeToCancel(null);
        setPendingStatus(null);
        dispatch(packagePlanActions.clearPlans());
        dispatch(packageTierActions.clearTiers());
        dispatch(packageChargePlanActions.clearChargePlans());
        void dispatch(packagePlanActions.fetchPlans(packageId, loadedVersionId));
        dispatch(packageScheduledChangeActions.clearScheduledChanges());
        void dispatch(packageScheduledChangeActions.fetchScheduledChanges(packageId, loadedVersionId));
    }, [dispatch, packageId, loadedVersionId]);

    // a slower response for a previous version can land after the current one
    const packagePlans = storedPlans !== undefined && storedPlans.versionId === loadedVersionId ? storedPlans.plans : undefined;
    const packageTiers = storedTiers !== undefined && storedTiers.versionId === loadedVersionId ? storedTiers.tiers : undefined;
    const packageChargePlans = storedChargePlans !== undefined && storedChargePlans.versionId === loadedVersionId ? storedChargePlans.chargePlans : undefined;
    const packageScheduledChanges = storedScheduledChanges !== undefined && storedScheduledChanges.versionId === loadedVersionId ? storedScheduledChanges.changes : undefined;
    const hasTiers = packageTiers !== undefined;
    const hasChargePlans = packageChargePlans !== undefined;

    // only fetched once the tab is opened; until then the badge counts the tiers in the package payload
    useEffect(() => {
        if (packageId === undefined || loadedVersionId === undefined || !isTiersTab || hasTiers) return;

        void dispatch(packageTierActions.fetchTiers(packageId, loadedVersionId));
    }, [dispatch, packageId, loadedVersionId, isTiersTab, hasTiers]);

    // same for the charge plans, so changes made in VOSS show without a page reload
    useEffect(() => {
        if (packageId === undefined || loadedVersionId === undefined || !isChargePlansTab || hasChargePlans) return;

        void dispatch(packageChargePlanActions.fetchChargePlans(packageId, loadedVersionId));
    }, [dispatch, packageId, loadedVersionId, isChargePlansTab, hasChargePlans]);

    useEffect(() => {
        if (isCopyingPlan && packages === undefined) void dispatch(packageActions.getPackages());
    }, [dispatch, isCopyingPlan, packages]);

    useEffect(() => {
        if (tierEditor === null || productCatalog !== undefined) return;

        void dispatch(packageTierActions.getProductCatalog()).then((catalog) => {
            if (catalog !== null) setProductCatalog(catalog);
        });
    }, [dispatch, tierEditor, productCatalog]);

    useEffect(() => {
        if (isSalesChannelsTab) void dispatch(packageUpdateActions.getSalesChannelCatalog());
    }, [dispatch, isSalesChannelsTab]);

    // the picker loads the plans of the package's current version, which is what the search hit points at
    const loadPlansForCopy = useCallback((source: PackageSearchHit) => dispatch(packagePlanActions.getPlans(source.id ?? '', source.versionId)), [dispatch]);

    if (packageId === undefined) return null;
    if (vossPackage === undefined || vossPackage === null)
        return isLoading === false ? (
            <p className="my-16">
                <i>{t('packages.packageNotLoaded')}</i>
            </p>
        ) : (
            <Spinner />
        );

    // a slower request for a previous package or version can land last; the version check only applies while a fetch is still
    // in flight, so a version VOSS resolves differently than asked cannot leave this spinning forever
    const sameId = (a: string | undefined, b: string | undefined) => a !== undefined && a.toLowerCase() === b?.toLowerCase();
    const isStale = !sameId(vossPackage.id, packageId) || (isLoading === true && versionId !== undefined && !sameId(vossPackage.versionId, versionId));
    if (isStale) return <Spinner />;

    const versions = packageVersions?.versions ?? [];
    const numberedVersions = numberVersions(versions);
    const selectedVersionId = versionId ?? vossPackage.versionId;
    const selectedVersion = versions.find((version) => version.versionId === selectedVersionId);
    const selectedVersionLabel = versionLabelOf(numberedVersions, selectedVersionId);
    const publishedVersionLabel = numberedVersions.find((version) => version.status === VossVersionStatus.Published && version.versionId !== vossPackage.versionId)?.label;

    const canEdit = vossPackage.status === VossVersionStatus.Draft || vossPackage.status === VossVersionStatus.Staged;
    // unlike plans, a Published version still has somewhere to go (Archived), so scheduling follows the transition table instead
    const canScheduleChanges = allowedTransitions(vossPackage.status).length > 0;

    const savePlan = (model: PackagePlanRequest) => {
        if (planEditor === null) return;
        setIsSavingPlan(true);

        const isNew = planEditor.plan?.id === undefined;
        const save = isNew
            ? packagePlanActions.createPlan(packageId, model, vossPackage.versionId)
            : packagePlanActions.updatePlan(packageId, planEditor.plan?.id ?? '', model, vossPackage.versionId);

        void dispatch(save)
            .then((saved) => {
                if (!saved) return;

                setPlanEditor(null);
                dispatch(commonActions.showSuccess(t(isNew ? 'packages.planCreated' : 'packages.planSaved')));
            })
            .finally(() => setIsSavingPlan(false));
    };

    const deletePlan = () => {
        if (planToDelete?.id === undefined) return;
        setIsDeletingPlan(true);

        void dispatch(packagePlanActions.deletePlan(packageId, planToDelete.id, vossPackage.versionId))
            .then((deleted) => {
                if (!deleted) return;

                closeModal();
                setPlanToDelete(null);
                dispatch(commonActions.showSuccess(t('packages.planDeleted')));
            })
            .finally(() => setIsDeletingPlan(false));
    };

    const deleteTier = () => {
        if (tierToDelete?.id === undefined) return;
        setIsDeletingTier(true);

        void dispatch(
            packageTierActions.deleteTier(packageId, tierToDelete.id, vossPackage.versionId, () => {
                closeModal();
                setTierToDelete(null);
                dispatch(commonActions.showSuccess(t('packages.tierDeleted')));
            })
        ).finally(() => setIsDeletingTier(false));
    };

    const saveTier = (draft: TierDraft) => {
        const tier = tierEditor?.tier;
        setIsSavingTier(true);

        if (tier?.id === undefined) {
            void dispatch(
                packageTierActions.createTier(packageId, toCreateTierRequest(draft), vossPackage.versionId, () => {
                    setTierEditor(null);
                    dispatch(commonActions.showSuccess(t('packages.tierCreated')));
                })
            ).finally(() => setIsSavingTier(false));
            return;
        }

        void dispatch(packageTierActions.saveTier(packageId, tier.id, planTierSave(tier, draft), vossPackage.versionId))
            .then((result) => {
                if (result === 'failed') return;
                // a half-applied draft can't be saved again (its new groups would be created twice), so the editor closes on the refetched tier
                setTierEditor(null);
                if (result === 'saved') dispatch(commonActions.showSuccess(t('packages.tierSaved')));
            })
            .finally(() => setIsSavingTier(false));
    };

    const reorderTiers = (changes: TierRankChange[]) => {
        // a move made while the previous one is saving would interleave their rank writes
        if (isReorderingTiers) return;
        setIsReorderingTiers(true);

        void dispatch(packageTierActions.reorderTiers(packageId, packageTiers ?? [], changes, vossPackage.versionId)).finally(() => setIsReorderingTiers(false));
    };

    // past Draft VOSS only takes names, external IDs, tags and sort orders on tiers and groups (so none can be added, removed or re-ranked), and no charge plan writes at all
    const canEditDraft = canEdit && isFullyEditable(vossPackage.status);

    // the package detail carries every list; one fetched on its own (for its tab or after a change) replaces it once loaded
    const tiers = packageTiers ?? vossPackage.tiers ?? [];
    const plans = packagePlans ?? vossPackage.plans ?? [];
    const chargePlans = packageChargePlans ?? vossPackage.chargePlans ?? [];

    const openChargePlan = openChargePlanId === null ? undefined : chargePlans.find((chargePlan) => chargePlan.id === openChargePlanId);
    const openChargePlanTier = tiers.find((tier) => tier.id === openChargePlan?.tierId);

    const saveRevenueRecognition = (productGroupId: string, mode: VossUseRevenueRecognition) => {
        if (openChargePlan?.id === undefined) return;
        setSavingRevenueGroupId(productGroupId);

        const model = withRevenueRecognition(openChargePlan, openChargePlanTier, productGroupId, mode);
        void dispatch(packageChargePlanActions.updateChargePlan(packageId, openChargePlan.id, model, vossPackage.versionId))
            .then((saved) => {
                if (saved) dispatch(commonActions.showSuccess(t('packages.revenueRecognitionSaved')));
            })
            .finally(() => setSavingRevenueGroupId(null));
    };

    const saveChargePlan = (draft: ChargePlanDraft) => {
        const chargePlanId = chargePlanEditor?.chargePlan?.id;
        setIsSavingChargePlan(true);

        const save =
            chargePlanId === undefined
                ? packageChargePlanActions.createChargePlan(packageId, toCreateChargePlanRequest(draft), vossPackage.versionId)
                : packageChargePlanActions.updateChargePlan(packageId, chargePlanId, toUpdateChargePlanRequest(draft), vossPackage.versionId);

        void dispatch(save)
            .then((saved) => {
                if (!saved) return;
                setChargePlanEditor(null);
                dispatch(commonActions.showSuccess(t(chargePlanId === undefined ? 'packages.chargePlanCreated' : 'packages.chargePlanSaved')));
            })
            .finally(() => setIsSavingChargePlan(false));
    };

    const deleteChargePlan = () => {
        const chargePlanId = chargePlanEditor?.chargePlan?.id;
        if (chargePlanId === undefined) return;
        setIsSavingChargePlan(true);

        void dispatch(packageChargePlanActions.deleteChargePlan(packageId, chargePlanId, vossPackage.versionId))
            .then((deleted) => {
                if (!deleted) return;
                closeModal();
                setChargePlanEditor(null);
                dispatch(commonActions.showSuccess(t('packages.chargePlanDeleted')));
            })
            .finally(() => setIsSavingChargePlan(false));
    };

    const chargePlanName = (chargePlan: PackageChargePlan | undefined) =>
        chargePlanTitle(
            tiers.find((tier) => tier.id === chargePlan?.tierId),
            plans.find((plan) => plan.id === chargePlan?.planId)
        );

    // the package detail already carries its sales channels, so they're only fetched again after a change
    const salesChannels = reloadedSalesChannels === undefined ? (vossPackage.salesChannels ?? []) : reloadedSalesChannels;

    const reloadSalesChannels = () => {
        // a change can land after the user has left this package, and its list must not replace the one shown now
        if (shownPackageIdRef.current !== packageId) return;

        salesChannelsReloadRef.current?.abort();
        const reload = new AbortController();
        salesChannelsReloadRef.current = reload;
        void dispatch(packageUpdateActions.getPackageSalesChannels(packageId, reload.signal));
    };

    const changeSalesChannel = (change: ReturnType<typeof packageUpdateActions.removePackageSalesChannel>, onSuccess: () => void) => {
        setIsSavingSalesChannel(true);
        void dispatch(change)
            .then((succeeded) => {
                if (!succeeded) return;

                onSuccess();
                reloadSalesChannels();
            })
            .finally(() => setIsSavingSalesChannel(false));
    };

    const saveSalesChannel = (salesChannelId: string, validity: UpdatePackageSalesChannelRequest) =>
        changeSalesChannel(
            salesChannelDrawer?.channel === undefined
                ? packageUpdateActions.addPackageSalesChannel(packageId, { id: salesChannelId, ...validity })
                : packageUpdateActions.updatePackageSalesChannel(packageId, salesChannelId, validity),
            () => setSalesChannelDrawer(undefined)
        );

    const removeSalesChannel = () => {
        if (channelToRemove?.id === undefined) return;

        changeSalesChannel(packageUpdateActions.removePackageSalesChannel(packageId, channelToRemove.id), () => {
            closeModal();
            setChannelToRemove(undefined);
        });
    };

    const saveScheduledChange = (model: PackageScheduledChangeRequest) => {
        if (scheduledChangeEditor === null) return;
        setIsSavingScheduledChange(true);

        const changeId = scheduledChangeEditor.change?.id;
        const save =
            changeId === undefined
                ? packageScheduledChangeActions.createScheduledChange(packageId, model, vossPackage.versionId)
                : packageScheduledChangeActions.updateScheduledChange(packageId, changeId, model, vossPackage.versionId);

        void dispatch(save)
            .then((saved) => {
                if (!saved) return;

                setScheduledChangeEditor(null);
                dispatch(commonActions.showSuccess(t(changeId === undefined ? 'packages.scheduledChangeCreated' : 'packages.scheduledChangeSaved')));
            })
            .finally(() => setIsSavingScheduledChange(false));
    };

    const cancelScheduledChange = () => {
        if (changeToCancel?.id === undefined) return;
        setIsCancellingChange(true);

        void dispatch(packageScheduledChangeActions.cancelScheduledChange(packageId, changeToCancel.id, vossPackage.versionId))
            .then((cancelled) => {
                if (!cancelled) return;

                closeModal();
                setChangeToCancel(null);
                dispatch(commonActions.showSuccess(t('packages.scheduledChangeCancelled')));
            })
            .finally(() => setIsCancellingChange(false));
    };

    const changeVersionStatus = () => {
        if (pendingStatus === null) return;
        setIsChangingStatus(true);

        void dispatch(packageUpdateActions.changePackageStatus(packageId, vossPackage, pendingStatus))
            .then((saved) => {
                // the modal can be dismissed while saving; a late response must not act on another package's page
                if (saved === null || shownPackageIdRef.current !== packageId) return;

                closeModal();
                setPendingStatus(null);
                // the version keeps its id, so the editor reset keyed on it does not run; editors opened under the old status would offer edits VOSS now rejects
                setIsEditing(false);
                setPlanEditor(null);
                setTierEditor(null);
                setOpenChargePlanId(null);
                setChargePlanEditor(null);
                setScheduledChangeEditor(null);
                dispatch(commonActions.showSuccess(t(versionTransitionConfig[pendingStatus].successKey, { version: selectedVersionLabel })));
            })
            .finally(() => setIsChangingStatus(false));
    };

    const createVersion = (comment: string) => {
        setIsCreatingVersion(true);

        void dispatch(packageUpdateActions.createPackageVersion(packageId, { versionId: vossPackage.versionId, comment: comment.length > 0 ? comment : undefined }))
            .then((created) => {
                if (created === null || shownPackageIdRef.current !== packageId) return;

                closeModal();
                dispatch(commonActions.showSuccess(t('packages.versionCreated')));
                if (created.versionId !== undefined) setSearchParams({ versionId: created.versionId });
            })
            .finally(() => setIsCreatingVersion(false));
    };

    const badgeSource = { ...vossPackage, plans, tiers, chargePlans, salesChannels, scheduledChanges: packageScheduledChanges ?? vossPackage.scheduledChanges };
    const tabs = PACKAGE_DETAIL_TABS.map((tab) => {
        const count = tab.countOn === undefined ? null : (badgeSource[tab.countOn] ?? []).length;

        return {
            id: tab.path,
            label: (
                <span className="d-inline-flex align-items-center gap-8">
                    <SpirisIcon name={tab.icon} size="sm" />
                    {t(tab.labelKey)}
                </span>
            ),
            badge: count !== null && tab.path === activePath ? <span className="badge">{count}</span> : count,
        };
    });

    return (
        <>
            <PackageDetailHeader
                vossPackage={vossPackage}
                versions={numberedVersions}
                selectedVersionId={selectedVersionId}
                onSelectVersion={(value) => setSearchParams({ versionId: value })}
                onShowHistory={() => setIsHistoryOpen(true)}
                onCreateVersion={() => openModal(Modals.CreatePackageVersion)}
                onChangeStatus={(target) => {
                    setPendingStatus(target);
                    openModal(Modals.ChangePackageVersionStatus);
                }}
            />
            <Tabs
                tabs={tabs}
                activeTab={tabs.find((tab) => tab.id === activePath) ?? tabs[0]}
                onTabChange={(tab: { id: string }) => void navigate({ pathname: `${basePath}/${tab.id}`, search: location.search })}
            />
            {isEditing && (
                <PackageEditDrawer
                    key={selectedVersionId}
                    vossPackage={vossPackage}
                    targetGroupOptions={targetGroups}
                    onClose={() => setIsEditing(false)}
                    isSaving={isSaving}
                    onSave={(model) => {
                        setIsSaving(true);
                        void dispatch(packageUpdateActions.updatePackage(packageId, model, vossPackage.versionId))
                            .then((saved) => {
                                if (saved !== null) setIsEditing(false);
                            })
                            .finally(() => setIsSaving(false));
                    }}
                />
            )}
            {planEditor !== null && (
                <PackagePlanEditorDrawer
                    key={planEditor.plan?.id ?? 'new'}
                    plan={planEditor.plan}
                    copySource={planEditor.copySource}
                    limitedEditing={!isFullyEditable(vossPackage.status)}
                    isSaving={isSavingPlan}
                    onClose={() => setPlanEditor(null)}
                    onSave={savePlan}
                />
            )}
            {isCopyingPlan && (
                <CopyPlanModal
                    packages={packages ?? []}
                    currentPackageId={packageId}
                    loadPlans={loadPlansForCopy}
                    onPick={(copySource) => {
                        closeModal();
                        setPlanEditor({ copySource });
                    }}
                />
            )}
            {isConfirmingPlanDelete && (
                <ConfirmDeleteModal title={t('packages.deletePlan') ?? ''} itemName={planToDelete.name ?? ''} isLoading={isDeletingPlan} onSubmit={deletePlan} />
            )}
            {isConfirmingTierDelete && (
                <ConfirmDeleteModal title={t('packages.deleteTier') ?? ''} itemName={tierToDelete.name ?? ''} isLoading={isDeletingTier} onSubmit={deleteTier} />
            )}
            {tierEditor !== null && (
                <PackageTierEditorDrawer
                    key={tierEditor.tier?.id ?? 'new'}
                    tier={tierEditor.tier}
                    initialRank={nextTierRank(tiers)}
                    limitedEditing={!isFullyEditable(vossPackage.status)}
                    products={productCatalog ?? []}
                    isSaving={isSavingTier}
                    onClose={() => setTierEditor(null)}
                    onSave={saveTier}
                />
            )}
            {openChargePlan !== undefined && (
                <PackageChargePlanDrawer
                    chargePlan={openChargePlan}
                    tier={openChargePlanTier}
                    plan={plans.find((plan) => plan.id === openChargePlan.planId)}
                    canEditRevenueRecognition={canEditDraft}
                    savingGroupId={savingRevenueGroupId}
                    onRevenueRecognitionChange={saveRevenueRecognition}
                    onClose={() => setOpenChargePlanId(null)}
                    onEdit={
                        canEditDraft
                            ? () => {
                                  setOpenChargePlanId(null);
                                  setChargePlanEditor({ chargePlan: openChargePlan });
                              }
                            : undefined
                    }
                />
            )}
            {chargePlanEditor !== null && (
                <PackageChargePlanEditorDrawer
                    key={chargePlanEditor.chargePlan?.id ?? 'new'}
                    chargePlan={chargePlanEditor.chargePlan}
                    initialTierId={chargePlanEditor.tierId}
                    initialPlanId={chargePlanEditor.planId}
                    tiers={tiers}
                    plans={plans}
                    chargePlans={chargePlans}
                    isSaving={isSavingChargePlan}
                    onClose={() => setChargePlanEditor(null)}
                    onSave={saveChargePlan}
                    onDelete={() => openModal(Modals.DeletePackageChargePlan)}
                />
            )}
            {isConfirmingChargePlanDelete && (
                <ConfirmDeleteModal
                    title={t('packages.deleteChargePlan') ?? ''}
                    itemName={chargePlanName(chargePlanEditor.chargePlan)}
                    isLoading={isSavingChargePlan}
                    onSubmit={deleteChargePlan}
                />
            )}
            {salesChannelDrawer !== undefined && (
                <PackageSalesChannelDrawer
                    channel={salesChannelDrawer.channel}
                    availableChannels={
                        Array.isArray(salesChannelCatalog)
                            ? salesChannelCatalog.filter((candidate) => !(salesChannels ?? []).some((linked) => linked.id === candidate.id))
                            : salesChannelCatalog
                    }
                    onClose={() => setSalesChannelDrawer(undefined)}
                    onSave={saveSalesChannel}
                    isSaving={isSavingSalesChannel}
                />
            )}
            {isConfirmingChannelRemove && (
                <PackageSalesChannelRemoveModal
                    channelName={channelToRemove.name ?? ''}
                    isRemoving={isSavingSalesChannel}
                    onRemove={removeSalesChannel}
                    onCancel={() => {
                        closeModal();
                        setChannelToRemove(undefined);
                    }}
                />
            )}
            {scheduledChangeEditor !== null && (
                <PackageScheduledChangeDrawer
                    key={scheduledChangeEditor.change?.id ?? 'new'}
                    change={scheduledChangeEditor.change}
                    currentStatus={vossPackage.status}
                    isSaving={isSavingScheduledChange}
                    onClose={() => setScheduledChangeEditor(null)}
                    onSave={saveScheduledChange}
                />
            )}
            {isConfirmingChangeCancel && <CancelScheduledChangeModal change={changeToCancel} isLoading={isCancellingChange} onConfirm={cancelScheduledChange} />}
            {isHistoryOpen && (
                <PackageVersionHistoryDrawer
                    packageName={vossPackage.name ?? ''}
                    versions={numberedVersions}
                    selectedVersionId={selectedVersionId}
                    onSelectVersion={(value) => {
                        setIsHistoryOpen(false);
                        setSearchParams({ versionId: value });
                    }}
                    onClose={() => setIsHistoryOpen(false)}
                />
            )}
            {isConfirmingStatusChange && (
                <PackageVersionStatusModal
                    vossPackage={vossPackage}
                    versionLabel={selectedVersionLabel}
                    targetStatus={pendingStatus}
                    replacedVersionLabel={publishedVersionLabel}
                    isSaving={isChangingStatus}
                    onConfirm={changeVersionStatus}
                />
            )}
            {isCreateVersionOpen && (
                <CreatePackageVersionModal sourceVersionLabel={selectedVersionLabel} sourceStatus={vossPackage.status} isSaving={isCreatingVersion} onCreate={createVersion} />
            )}
            <div className="mt-24">
                <Routes>
                    <Route index element={<PackageOverviewTab vossPackage={vossPackage} selectedVersion={selectedVersion} canEdit={canEdit} onEdit={() => setIsEditing(true)} />} />
                    <Route
                        path="plans"
                        element={
                            <PackagePlansTab
                                plans={packagePlans}
                                isLoading={isLoadingPlans}
                                canEdit={canEdit}
                                onAdd={() => setPlanEditor({})}
                                onCopy={() => openModal(Modals.CopyPackagePlan)}
                                onEdit={(plan) => setPlanEditor({ plan })}
                                onDelete={(plan) => {
                                    setPlanToDelete(plan);
                                    openModal(Modals.DeletePackagePlan);
                                }}
                            />
                        }
                    />
                    <Route
                        path="tiers"
                        element={
                            <PackageTiersTab
                                tiers={packageTiers}
                                isLoading={isLoadingTiers}
                                onAdd={canEditDraft ? () => setTierEditor({}) : undefined}
                                onEdit={canEdit ? (tier) => setTierEditor({ tier }) : undefined}
                                onDelete={
                                    canEditDraft
                                        ? (tier) => {
                                              setTierToDelete(tier);
                                              openModal(Modals.DeletePackageTier);
                                          }
                                        : undefined
                                }
                                onReorder={canEditDraft ? reorderTiers : undefined}
                            />
                        }
                    />
                    <Route
                        path="sales-channels"
                        element={
                            <PackageSalesChannelsTab
                                salesChannels={salesChannels}
                                catalog={salesChannelCatalog ?? []}
                                onAdd={() => setSalesChannelDrawer({})}
                                onEdit={(channel) => setSalesChannelDrawer({ channel })}
                                onRemove={(channel) => {
                                    setChannelToRemove(channel);
                                    openModal(Modals.ConfirmDeleteModal);
                                }}
                            />
                        }
                    />
                    <Route
                        path="charge-plans"
                        element={
                            <PackageChargePlansTab
                                tiers={tiers}
                                plans={plans}
                                chargePlans={chargePlans}
                                onOpen={(chargePlan) => setOpenChargePlanId(chargePlan.id ?? null)}
                                onAdd={canEditDraft ? () => setChargePlanEditor({}) : undefined}
                                onConfigure={canEditDraft ? (tier, plan) => setChargePlanEditor({ tierId: tier.id, planId: plan.id }) : undefined}
                            />
                        }
                    />
                    <Route
                        path="scheduled-changes"
                        element={
                            <PackageScheduledChangesTab
                                changes={packageScheduledChanges}
                                isLoading={isLoadingScheduledChanges}
                                canSchedule={canScheduleChanges}
                                onAdd={() => setScheduledChangeEditor({})}
                                onEdit={(change) => setScheduledChangeEditor({ change })}
                                onCancel={(change) => {
                                    setChangeToCancel(change);
                                    openModal(Modals.CancelPackageScheduledChange);
                                }}
                            />
                        }
                    />
                    <Route
                        path="*"
                        element={
                            <p className="my-16">
                                <i>{t('packages.tabNotImplemented')}</i>
                            </p>
                        }
                    />
                </Routes>
            </div>
        </>
    );
};

export default PackageDetailContainer;
